import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, Globe, ArrowRight, Stethoscope, Pill, Scan, Syringe, Heart } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { loadStaticData } from '@/lib/offlineData';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ProcedurePrice {
  us_code: string;
  country_code: string;
  price_usd: number;
}

interface ProcedureName {
  code: string;
  name: string;
  llm_quick_name?: string;
}

interface USPrice {
  cpt_code: string;
  avg_gross_charge: number;
}

interface CountryInfo {
  country_code: string;
  country_name: string;
  flag_emoji: string;
  healthcare_system: string;
  currency_code: string;
}

interface BundleItem {
  code: string;
  name: string;
  intlPrice: number;
  usPrice: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatUSD(amount: number): string {
  if (!amount || !isFinite(amount)) return '$0';
  if (amount >= 10000) return `$${(amount / 1000).toFixed(0)}k`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  return `$${amount.toFixed(0)}`;
}

// ─── Well-known procedure groups for the challenge ──────────────────────────

const CHALLENGE_CATEGORIES = [
  {
    label: 'Doctor Visit',
    icon: Stethoscope,
    codes: ['99213', '99214'],
    description: 'Office visit with your doctor',
  },
  {
    label: 'Blood Work',
    icon: Syringe,
    codes: ['85025', '80053', '80061'],
    description: 'CBC, metabolic panel, lipid panel',
  },
  {
    label: 'Imaging',
    icon: Scan,
    codes: ['71046', '73030'],
    description: 'Chest X-ray, knee X-ray',
  },
  {
    label: 'Prescription',
    icon: Pill,
    codes: ['99213'], // placeholder — concept is the visit
    description: 'Basic prescription visit',
  },
];

// ─── Main Component ─────────────────────────────────────────────────────────

export default function HundredDollarChallenge() {
  const brand = useBrand();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const BUDGET = 100;

  const { data: intlData, isLoading } = useQuery({
    queryKey: ['international-stats-100'],
    queryFn: () => loadStaticData('international_stats.json'),
    staleTime: 1000 * 60 * 60,
  });

  const countries = useMemo(() => {
    if (!intlData) return [];
    return ((intlData as any).countries as CountryInfo[])?.sort((a, b) =>
      a.country_name.localeCompare(b.country_name)
    ) || [];
  }, [intlData]);

  const prices = useMemo(() => {
    if (!intlData) return new Map<string, Map<string, number>>();
    const map = new Map<string, Map<string, number>>();
    ((intlData as any).procedurePrices as ProcedurePrice[] || []).forEach(p => {
      if (!map.has(p.country_code)) map.set(p.country_code, new Map());
      map.get(p.country_code)!.set(p.us_code, p.price_usd);
    });
    return map;
  }, [intlData]);

  const procedureNames = useMemo(() => {
    if (!intlData) return new Map<string, string>();
    const map = new Map<string, string>();
    ((intlData as any).procedureNames as ProcedureName[] || []).forEach(p => {
      map.set(p.code, p.llm_quick_name || p.name);
    });
    return map;
  }, [intlData]);

  const usPrices = useMemo(() => {
    if (!intlData) return new Map<string, number>();
    const map = new Map<string, number>();
    ((intlData as any).usPricing as USPrice[] || []).forEach(p => {
      map.set(p.cpt_code, p.avg_gross_charge);
    });
    return map;
  }, [intlData]);

  // ── For each country, calculate what $100 buys ────────────────────────
  const countryBundles = useMemo(() => {
    if (!prices.size || !usPrices.size) return [];

    return countries.map(country => {
      const cp = prices.get(country.country_code);
      if (!cp) return null;

      // Greedily add procedures (cheapest first) until we exceed $100
      const available: { code: string; price: number; usPrice: number; name: string }[] = [];
      cp.forEach((price, code) => {
        if (price > 0 && price <= BUDGET) {
          const usP = usPrices.get(code) || 0;
          const name = procedureNames.get(code) || code;
          available.push({ code, price, usPrice: usP, name });
        }
      });

      // Sort by price ascending
      available.sort((a, b) => a.price - b.price);

      // Greedy knapsack
      const bundle: BundleItem[] = [];
      let remaining = BUDGET;
      let totalUsPrice = 0;

      for (const item of available) {
        if (item.price <= remaining) {
          bundle.push({
            code: item.code,
            name: item.name,
            intlPrice: item.price,
            usPrice: item.usPrice,
          });
          remaining -= item.price;
          totalUsPrice += item.usPrice;
          if (bundle.length >= 20) break; // Cap display
        }
      }

      return {
        country,
        bundle,
        totalIntlCost: BUDGET - remaining,
        totalUsCost: totalUsPrice,
        procedureCount: bundle.length,
        remaining,
      };
    })
    .filter(Boolean)
    .filter(c => c!.procedureCount >= 2)
    .sort((a, b) => b!.procedureCount - a!.procedureCount) as NonNullable<typeof countryBundles[0]>[];
  }, [countries, prices, usPrices, procedureNames]);

  // US comparison — what does $100 buy in the US?
  const usBundle = useMemo(() => {
    const available: { code: string; price: number; name: string }[] = [];
    usPrices.forEach((price, code) => {
      if (price > 0 && price <= BUDGET) {
        available.push({ code, price, name: procedureNames.get(code) || code });
      }
    });
    available.sort((a, b) => a.price - b.price);

    const bundle: { code: string; name: string; price: number }[] = [];
    let remaining = BUDGET;
    for (const item of available) {
      if (item.price <= remaining) {
        bundle.push(item);
        remaining -= item.price;
        if (bundle.length >= 10) break;
      }
    }
    return { bundle, total: BUDGET - remaining, count: bundle.length };
  }, [usPrices, procedureNames]);

  const selectedBundle = selectedCountry
    ? countryBundles.find(c => c.country.country_code === selectedCountry)
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>The $100 Challenge — What Can You Get? | {brand.name}</title>
        <meta name="description" content="What healthcare can $100 buy around the world? Compare what you get for $100 in the US vs 32 countries using real government prices." />
      </Helmet>

      {/* Hero */}
      <section className="border-b border-border/40 bg-gradient-to-b from-green-500/5 to-background">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:py-16 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/10 border-2 border-green-500/30 mb-6">
            <DollarSign className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            The <span className="text-green-400">$100</span> Challenge
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-2">
            What healthcare can $100 buy around the world?
          </p>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Using actual government fee schedules from 32 countries — not estimates.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-20">
            <Globe className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading global pricing data...</p>
          </div>
        ) : (
          <>
            {/* US Baseline */}
            <Card className="mb-10 border-red-500/30 bg-red-500/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">🇺🇸</span>
                  <div>
                    <h2 className="text-xl font-bold">United States</h2>
                    <p className="text-sm text-muted-foreground">What $100 buys at US hospital chargemaster prices</p>
                  </div>
                </div>
                {usBundle.count === 0 ? (
                  <p className="text-lg text-red-400 font-semibold">
                    Almost nothing. The average office visit alone costs more than $100.
                  </p>
                ) : (
                  <div>
                    <p className="text-sm text-muted-foreground mb-2">{usBundle.count} procedure{usBundle.count !== 1 ? 's' : ''} for ${usBundle.total.toFixed(0)}</p>
                    <div className="flex flex-wrap gap-2">
                      {usBundle.bundle.map(item => (
                        <Badge key={item.code} variant="outline" className="text-xs">
                          {item.name} (${item.price.toFixed(0)})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Country Grid */}
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              Same $100 in other countries
            </h2>

            {selectedBundle ? (
              /* Expanded country view */
              <div>
                <button
                  onClick={() => setSelectedCountry(null)}
                  className="text-primary hover:underline mb-4 flex items-center gap-1"
                >
                  ← Back to all countries
                </button>
                <Card className="border-green-500/30 bg-green-500/5">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl">{selectedBundle.country.flag_emoji}</span>
                      <div>
                        <h2 className="text-xl font-bold">{selectedBundle.country.country_name}</h2>
                        <p className="text-sm text-muted-foreground">{selectedBundle.country.healthcare_system}</p>
                      </div>
                      <div className="ml-auto text-right">
                        <p className="text-3xl font-bold text-green-400">{selectedBundle.procedureCount}</p>
                        <p className="text-xs text-muted-foreground">procedures for $100</p>
                      </div>
                    </div>

                    <div className="space-y-2 mt-6">
                      {selectedBundle.bundle.map((item, idx) => (
                        <div key={item.code} className="flex items-center justify-between py-2 border-b border-border/30 last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-6">{idx + 1}.</span>
                            <span className="text-sm">{item.name}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm font-medium text-green-400">${item.intlPrice.toFixed(2)}</span>
                            {item.usPrice > 0 && (
                              <span className="text-xs text-red-400/60 line-through">${formatUSD(item.usPrice)} US</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 pt-4 border-t border-border/40 flex justify-between items-center">
                      <div>
                        <p className="text-sm text-muted-foreground">Total spent</p>
                        <p className="font-bold">${selectedBundle.totalIntlCost.toFixed(2)} of $100</p>
                      </div>
                      {selectedBundle.totalUsCost > 0 && (
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">Same in US would cost</p>
                          <p className="font-bold text-red-400">{formatUSD(selectedBundle.totalUsCost)}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              /* Country cards grid */
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {countryBundles.map(item => (
                  <Card
                    key={item.country.country_code}
                    className="cursor-pointer hover:border-green-500/50 transition-all hover:scale-[1.02]"
                    onClick={() => setSelectedCountry(item.country.country_code)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-2xl">{item.country.flag_emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{item.country.country_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{item.country.healthcare_system}</p>
                        </div>
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="text-3xl font-bold text-green-400">{item.procedureCount}</p>
                          <p className="text-xs text-muted-foreground">procedures</p>
                        </div>
                        <div className="text-right">
                          {item.totalUsCost > 0 && (
                            <>
                              <p className="text-sm text-red-400">{formatUSD(item.totalUsCost)}</p>
                              <p className="text-xs text-muted-foreground">same in US</p>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        Tap to see details <ArrowRight className="w-3 h-3" />
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Bottom links */}
            <div className="mt-16 text-center border-t border-border/40 pt-8">
              <p className="text-sm text-muted-foreground mb-4">
                All prices from official government fee schedules. US prices from hospital chargemaster data.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Link to="/international/calculator">
                  <Button variant="outline" size="sm">
                    <Heart className="w-4 h-4 mr-2" />
                    Scenario Calculator
                  </Button>
                </Link>
                <Link to="/international">
                  <Button variant="outline" size="sm">
                    <Globe className="w-4 h-4 mr-2" />
                    Full Comparison
                  </Button>
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
